// src/pages/ExperimentPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Card, Button, ProgressBar, Alert, Spinner,
  Modal, Form, Toast, ToastContainer
} from 'react-bootstrap';
import { Share2 } from 'lucide-react';
import JobSelectionStage from '../components/Experiment/JobSelectionStage';
import PlateSchemaStage from '../components/Experiment/PlateSchemaStage';
import DataAnalysisStage from '../components/Experiment/DataAnalysisStage';
import ResultsVisualizationStage from '../components/Experiment/ResultsVisualizationStage';
import ShareExperimentModal from '../components/Experiment/ShareExperimentModal';
import { getExperiment, updateExperiment as updateExperimentModel, createExperiment,
         getPlateSchema, updatePlateSchema, createPlateSchema,
         generateExperimentData, getExperimentData, updateExperimentData
        } from '../api/photoProcessingApi';
import './ExperimentPage.css';

const ExperimentPage = () => {
  const { experimentId } = useParams();
  const navigate = useNavigate();

  // Experiment state
  const [currentStage, setCurrentStage] = useState(0);
  const [experiment, setExperiment] = useState({
    id: experimentId || null,
    name: '',
    description: '',
    date: new Date().toISOString(),
    selectedJobs: [],
    plateSchema: {
      id: null,
      rows: 8,
      cols: 12,
      samples: [],
      classes: []
    },
    experimentData: null,
    analysis: null,
    created_at: '',
    updated_at: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [showNameModal, setShowNameModal] = useState(false);
  const [nameValidation, setNameValidation] = useState({
    isValid: false,
    message: '',
    isTouched: false
  });

  // Track which stages have been completed
  const [completedStages, setCompletedStages] = useState(new Set());

  const [showShareModal, setShowShareModal] = useState(false);

  const hasShownExportDialogRef = useRef(true);

  // Load experiment data table
  useEffect(() => {
    if (experimentId) {
      loadExperiment();
    }
  }, [experimentId]);

  // Validate experiment name
  useEffect(() => {
    validateExperimentName(experiment.name);
  }, [experiment.name]);

  // Update completed stages when conditions are met
  useEffect(() => {
    const newCompletedStages = new Set(completedStages);

    // Check if current stage can be marked as completed
    if (canProceedFromStage(currentStage)) {
      newCompletedStages.add(currentStage);
    } else {
      newCompletedStages.delete(currentStage);
    }

    setCompletedStages(newCompletedStages);
  }, [experiment, currentStage]);

  const validateExperimentName = (name) => {
    const trimmedName = name.trim();
    let isValid = true;
    let message = '';

    if (!trimmedName) {
      isValid = false;
      message = 'Experiment name is required';
    } else if (trimmedName.length < 3) {
      isValid = false;
      message = 'Name must be at least 3 characters long';
    } else if (trimmedName.length > 200) {
      isValid = false;
      message = 'Name cannot exceed 200 characters';
    } else if (!/^[a-zA-Zа-яёА-ЯЁ0-9\s\-_()]+$/.test(trimmedName)) {
      isValid = false;
      message = 'Name can only contain letters, numbers, spaces, hyphens, underscores, and parentheses';
    }

    setNameValidation({
      isValid,
      message,
      isTouched: nameValidation.isTouched || name.length > 0
    });

    return isValid;
  };

  const loadExperiment = async () => {
  
    try {
      setLoading(true);
      setError(null);

      let plateSchemaData = null;
      let experimentData, experimentDataData;
      let schemaLoadFailed = false;

      experimentData = await getExperiment(experimentId);
      if (experimentData) {
        try {
          plateSchemaData = await getPlateSchema(experimentData.plate_schema);
        } catch (schemaError) {
          console.warn('Failed to load plate schema:', schemaError);
          schemaLoadFailed = true;
          // Don't throw, just continue with null schema
        }

        if (experimentData.data) {
          try {
            experimentDataData = await getExperimentData(experimentData.id);
          } catch (experimentDataError) {
            throw new Error(experimentDataError?.response?.data?.error || 'Failed to load experiment table data when loading experiment');
          }
        }

        const loadedExperiment = {
          ...experiment,
          id: experimentData.id,
          name: experimentData.name,
          description: experimentData.description,
          date: experimentData.date,
          plateSchema: plateSchemaData, // This will be null if loading failed
          selectedJobs: experimentData.selected_jobs,
          experimentData: experimentDataData,
          created_at: experimentData.created_at,
          updated_at: experimentData.updated_at
        };
        setExperiment(loadedExperiment);
        setHasUnsavedChanges(false);

        // Determine which stages are completed based on loaded data
        const initialCompletedStages = new Set();
        if (loadedExperiment.selectedJobs.length > 0 && validateExperimentName(loadedExperiment.name)) {
          initialCompletedStages.add(0);
        }
        if (loadedExperiment.plateSchema?.samples?.length > 0) {
          initialCompletedStages.add(1);
        }
        if (loadedExperiment.experimentData?.status === 'completed') {
          initialCompletedStages.add(2);
        }
        initialCompletedStages.add(3); // Results stage is always accessible

        setCompletedStages(initialCompletedStages);

        // Set a warning error if schema loading failed
        if (schemaLoadFailed) {
          setError('Experiment loaded but plate schema could not be retrieved. Some features may be limited.');
        }
      }

    } catch (error) {
      console.error('Error loading experiment:', error);
      setError('Failed to load experiment: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const stages = [
    { id: 0, name: 'Select Images', icon: 'bi-images', description: 'Choose and order analysis jobs' },
    { id: 1, name: 'Plate Schema', icon: 'bi-grid-3x3', description: 'Define sample layout' },
    { id: 2, name: 'Data Analysis', icon: 'bi-bar-chart', description: 'Analyze and visualize results' },
    { id: 3, name: 'Results & Plots', icon: 'bi-graph-up', description: 'View analysis results and visualizations' }
  ];

  const canProceedFromStage = (stage) => {
    switch (stage) {
      case 0:
        return experiment.selectedJobs.length > 0 && nameValidation.isValid;
      case 1:
        return experiment.plateSchema?.samples?.length > 0;
      case 2:
        return experiment.experimentData?.status === 'completed';
      case 3:
        return true; // Results stage is always accessible
      default:
        return false;
    }
  };

  // Check if a specific stage can be accessed
  const canAccessStage = (targetStage) => {
    if (targetStage === 3) return true;

    // Stage 2 (Data) is always accessible (in order to allow import)
    if (targetStage === 2) return true;

    // Stage 0 is always accessible (Select Images)
    if (targetStage === 0) return true;

    // Check if all previous stages are completed
    for (let i = 0; i < targetStage; i++) {
      if (!completedStages.has(i)) {
        return false;
      }
    }

    return true;
  };

  const handleStageNavigation = (targetStage) => {
    // Save unsaved changes if moving away from current stage
    if (hasUnsavedChanges && targetStage !== currentStage) {
      handleSaveAdjustments();
    }

    // Check if we can access the target stage
    if (canAccessStage(targetStage)) {
      setCurrentStage(targetStage);
    } else {
      // Show appropriate error message
      let stageName = stages[targetStage].name;
      setError(`Cannot navigate to ${stageName}. Please complete previous stages first.`);
      setToastMessage(`Cannot navigate to ${stageName}. Please complete previous stages first.`);
      setToastVariant('danger');
      setShowToast(true);
    }
  };

  const handleNextStage = () => {
    if (currentStage < stages.length - 1) {
      const nextStage = currentStage + 1;
      if (canAccessStage(nextStage)) {
        if (hasUnsavedChanges) {
          handleSaveAdjustments();
        }
        setCurrentStage(nextStage);
      } else {
        setError(`Cannot proceed to ${stages[nextStage].name}. Please complete current stage first.`);
        setToastMessage(`Cannot proceed to ${stages[nextStage].name}. Please complete current stage first.`);
        setToastVariant('danger');
        setShowToast(true);
      }
    }
  };

  const handlePreviousStage = () => {
    if (currentStage > 0) {
      const prevStage = currentStage - 1;
      if (canAccessStage(prevStage)) {
        if (hasUnsavedChanges) {
          handleSaveAdjustments();
        }
        if (prevStage === 2) {
          hasShownExportDialogRef.current = false;
        }
        setCurrentStage(prevStage);
      }
    }
  };

  const updateExperiment = (updates) => {
    setExperiment(prev => {
      const newExperiment = { ...prev, ...updates };

      // Handle nested updates properly
      if (updates.selectedJobs) {
        newExperiment.selectedJobs = updates.selectedJobs;
      }
      if (updates.plateSchema) {
        newExperiment.plateSchema = { ...prev.plateSchema, ...updates.plateSchema };
      }
      if (updates.experimentData) {
        newExperiment.experimentData = updates.experimentData;
      }
      if (updates.analyses) {
        newExperiment.analyses = updates.analyses;
      }

      return newExperiment;
    });
    setHasUnsavedChanges(true);
  };

  const handleSaveAdjustments = async () => {
    if (!validateExperimentName(experiment.name)) {
      setShowNameModal(true);
      return;
    }

    try {
      setSaving(true);
      const experimentToUpdate = {
        name: experiment.name.trim(),
        description: experiment.description,
        date: experiment.date,
        selected_jobs: experiment.selectedJobs,
        plate_schema: experiment.plateSchema?.id || null
      };

      const schemaToUpdate = {...experiment.plateSchema};

      const experimentDataToUpdate = {
        columns_metadata: experiment.experimentData?.columns_metadata,
        rows_metadata: experiment.experimentData?.rows_metadata,
        data_matrix: experiment.experimentData?.data_matrix
      };

      let experimentDataResponse;
      let schemaResponse;
      let response;
      if (experimentId) {
        try {
            schemaResponse = await updatePlateSchema(experiment.plateSchema.id, experiment.plateSchema);
        } catch (schemaError) {
            throw new Error(schemaError?.response?.data?.error || 'Error updating schema when updating experiment');
        }
        response = await updateExperimentModel(experimentId, experimentToUpdate);
        if (experiment.experimentData) {
            experimentDataResponse = await updateExperimentData(experimentId, experimentDataToUpdate);
        }

      } else {
        try {
            schemaResponse = await createPlateSchema(schemaToUpdate);
        } catch (schemaError) {
            throw new Error(schemaError?.response?.data?.error || 'Error creating schema when creating experiment');
        }
        response = await createExperiment({...experimentToUpdate, plate_schema: schemaResponse.id});
        // Update with the new ID if it's a new experiment
        if (response.id) {
          setExperiment(prev => ({ ...prev, id: response.id, plateSchema: schemaResponse }));
          navigate(`/experiment/${response.id}`, { replace: true });
        }
      }

      setHasUnsavedChanges(false);
      setToastMessage('Experiment saved successfully!');
      setToastVariant('success');
      setShowToast(true);

      // Mark current stage as completed if it meets conditions
      if (canProceedFromStage(currentStage)) {
        setCompletedStages(prev => new Set([...prev, currentStage]));
      }
    } catch (error) {
      console.error('Error saving experiment:', error);
      setError('Failed to save experiment: ' + error.message);
      setToastMessage(`Failed to save experiment: ${error.message}`);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  const handleNameChange = (name) => {
    updateExperiment({ name });
    setNameValidation(prev => ({ ...prev, isTouched: true }));
  };

  const renderCurrentStage = () => {
    switch (currentStage) {
      case 0:
        return (
          <JobSelectionStage
            name={experiment.name}
            description={experiment.description}
            date={experiment.date}
            selectedJobs={experiment.selectedJobs}
            jobsCalculateTypes={experiment.jobsCalculateTypes}
            onNameChange={handleNameChange}
            onDescriptionChange={(description) => updateExperiment({ description })}
            onDateChange={(date) => updateExperiment({ date })}
            onJobsChange={(jobs) => updateExperiment({ selectedJobs: jobs })}
            nameValidation={nameValidation}
          />
        );
      case 1:
        return (
          <PlateSchemaStage
            plateSchema={experiment.plateSchema}
            selectedJobs={experiment.selectedJobs}
            onSchemaChange={(schema) => updateExperiment({ plateSchema: schema })}
          />
        );
      case 2:
        return (
          <DataAnalysisStage
            experiment={experiment}
            onExperimentUpdate={updateExperiment}
            hasShownExportDialogRef={hasShownExportDialogRef}
          />
        );
      case 3:
        return (
          <ResultsVisualizationStage
            experiment={experiment}
            onExperimentUpdate={updateExperiment}
          />
        );
      default:
        return null;
    }
  };

  // Get stage accessibility status
  const getStageStatus = (stageId) => {
    if (stageId === currentStage) return 'active';
    if (completedStages.has(stageId)) return 'completed';
    if (canAccessStage(stageId)) return 'accessible';
    return 'locked';
  };

  return (
    <div className="experiment-page">
      <Container fluid>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2>Chemical Plate Analysis</h2>
                <p className="text-muted">
                  {experiment.name || 'New Experiment'} - {stages[currentStage].description}
                </p>
                {!nameValidation.isValid && nameValidation.isTouched && (
                    <Alert variant="warning" className="py-2 mb-0 mt-2">
                      <small>
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {nameValidation.message}
                      </small>
                    </Alert>
                )}
              </div>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" onClick={() => navigate('/experiments')}>
                  <i className="bi bi-arrow-left me-2"></i>
                  Back to Experiments
                </Button>

                {/* Share button - only show for existing experiments */}
                {experimentId && (
                    <Button
                        variant="outline-success"
                        onClick={() => setShowShareModal(true)}
                        className="d-flex align-items-center gap-2"
                    >
                      <Share2 size={18}/>
                      Share
                    </Button>
                )}

                <Button
                    variant="primary"
                    onClick={handleSaveAdjustments}
                    disabled={saving || (!hasUnsavedChanges && experimentId)}
                    className="save-button"
                >
                  {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2"/>
                        Saving...
                      </>
                  ) : (
                      <>
                        <i className="bi bi-save me-2"></i>
                        {experimentId ? 'Save Changes' : 'Create Experiment'}
                      </>
                  )}
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Progress indicator */}
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Body className="py-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  {stages.map((stage, index) => {
                    const status = getStageStatus(index);
                    const isAccessible = status !== 'locked';
                    return (
                        <div
                            key={stage.id}
                            className={`stage-indicator ${index === currentStage ? 'active' : ''} ${completedStages.has(index) ? 'completed' : ''}`}
                            onClick={() => isAccessible && handleStageNavigation(index)}
                            style={{
                              cursor: isAccessible ? 'pointer' : 'default',
                              opacity: isAccessible ? 1 : 0.6
                            }}
                      >
                        <div className="stage-icon">
                          <i className={stage.icon}></i>
                          {!isAccessible && (
                            <span className="validation-badge bg-secondary" style={{ fontSize: '10px' }}>
                              <i className="bi bi-lock"></i>
                            </span>
                          )}
                          {index === 0 && !nameValidation.isValid && nameValidation.isTouched && (
                            <span className="validation-badge bg-danger">!</span>
                          )}
                        </div>
                        <div className="stage-info">
                          <div className="stage-name">{stage.name}</div>
                          <div className="stage-description">{stage.description}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <ProgressBar
                  now={((currentStage + 1) / stages.length) * 100}
                  variant={canProceedFromStage(currentStage) ? 'primary' : 'warning'}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Main content */}
        <Row>
          <Col>
            <Card className="main-content-card">
              <Card.Body>
                {renderCurrentStage()}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Navigation - Simplified to match previous version */}
        <div className="sticky-navigation">
          <Row className="mt-4">
            <Col>
              <div className="d-flex justify-content-between">
                <Button
                  variant="outline-secondary"
                  onClick={handlePreviousStage}
                  disabled={currentStage === 0}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Previous
                </Button>

                <Button
                  variant="primary"
                  onClick={handleNextStage}
                  disabled={!canAccessStage(currentStage + 1) || currentStage === stages.length - 1}
                >
                  Next
                  <i className="bi bi-arrow-right ms-2"></i>
                </Button>
              </div>
            </Col>
          </Row>
        </div>

        {/* Name Validation Modal */}
        <Modal show={showNameModal} onHide={() => setShowNameModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Invalid Experiment Name</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {nameValidation.message || 'Please provide a valid experiment name.'}
            </Alert>
            <Form.Group>
              <Form.Label>Experiment Name *</Form.Label>
              <Form.Control
                type="text"
                value={experiment.name}
                onChange={(e) => handleNameChange(e.target.value)}
                isInvalid={!nameValidation.isValid && nameValidation.isTouched}
                placeholder="Enter a descriptive name for your experiment"
              />
              <Form.Control.Feedback type="invalid">
                {nameValidation.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowNameModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (validateExperimentName(experiment.name)) {
                  setShowNameModal(false);
                  handleSaveAdjustments();
                }
              }}
              disabled={!nameValidation.isValid}
            >
              Save Anyway
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Toast Notifications */}
        <ToastContainer position="top-end" className="p-3">
          <Toast
            show={showToast}
            onClose={() => setShowToast(false)}
            delay={5000}
            autohide
            bg={toastVariant}
          >
            <Toast.Header>
              <strong className="me-auto">
                {toastVariant === 'success' ? 'Success' : 'Error'}
              </strong>
            </Toast.Header>
            <Toast.Body className="text-white">
              {toastMessage}
            </Toast.Body>
          </Toast>
        </ToastContainer>
      </Container>
      {/* Share Experiment Modal */}
      {experimentId && (
        <ShareExperimentModal
          show={showShareModal}
          onHide={() => setShowShareModal(false)}
          experiment={experiment}
          onShareUpdated={() => {
            // Refresh experiment data to update any share-related UI
            loadExperiment();
          }}
        />
      )}
    </div>
  );}
export default ExperimentPage;